import React, { FC } from 'react';
import { Button, Datagrid, FunctionField, ReferenceManyField, useTranslate } from 'react-admin';
import H2 from '~/components/H2';
import { time, Longdash } from '~/utils';
import RaPagination from '~/components/Pagination/RaPagination';
import { perPageDefault, rowsPerPageOptionsDefault } from '~/utils/pagination';
import { Individual } from '~/types/individual';
import Grid from '@material-ui/core/Grid';
import { SvgIcon } from '@material-ui/core';
import { ArrowForwardCircleIcon } from '~/img';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { filterChange } from '~/ducks/virtualizedList';
import { resource } from '~/resources/events';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { LegalConsentDecision } from '~/types/LegalConsent';

type LegalConsentsTabProps = {
  record?: Individual;
  [key: string]: any;
};

const LegalConsentsTab: FC<LegalConsentsTabProps> = (props) => {
  const { record } = props;
  const translate = useTranslate();
  const t = (key: string): string => translate(`resources.individuals.legalConsents.${key}`);
  const classes = useStyles();

  const history = useHistory();
  const dispatch = useDispatch();
  const handleViewAllEventsClick = () => {
    if (history) {
      dispatch(
        filterChange({
          resource,
          filters: {
            timestampFrom: null,
            timestampTo: null,
            objectId: record?.id,
            topic: 'individual.legalConsent',
          },
        })
      );
      history.push('/events?withFilters');
    }
  };

  return (
    <>
      <Grid container justify='space-between' alignItems='center' className={classes.root}>
        <Grid item>
          <H2 topSpacing={0} bottomSpacing={0}>
            {t('title')}
          </H2>
        </Grid>
        <Grid item id='section-actions'>
          <Button label={t('viewAllDecisions')} onClick={handleViewAllEventsClick}>
            <SvgIcon>
              <ArrowForwardCircleIcon />
            </SvgIcon>
          </Button>
        </Grid>
      </Grid>
      <ReferenceManyField
        label=''
        reference='legalConsents'
        target='individuals'
        sort={{ field: 'created', order: 'DESC' }}
        pagination={
          <RaPagination
            defaultPerPage={perPageDefault}
            rowsPerPageOptions={rowsPerPageOptionsDefault}
          />
        }
        perPage={perPageDefault}
        {...props}
      >
        <Datagrid size='small'>
          <FunctionField
            label={t('type')}
            sortable={false}
            render={(record: any) => record?.legalConsent?.type ?? Longdash}
          />
          <FunctionField
            label={t('version')}
            sortable={false}
            render={(record: any) => record?.legalConsent?.version ?? Longdash}
          />
          <FunctionField
            label={t('accepted')}
            sortable={false}
            render={(record: any) =>
              record?.accepted != null
                ? translate(`utils.${record.accepted ? 'yes' : 'no'}`)
                : Longdash
            }
          />
          <FunctionField
            label={t('validFrom')}
            sortable={false}
            render={(record: LegalConsentDecision) => {
              if (record?.legalConsent?.validityType === 'INFINITE' && record?.timestamp) {
                return time(record.timestamp).format('lll');
              }
              return Longdash;
            }}
          />
          <FunctionField label={t('validTill')} sortable={false} render={() => Longdash} />
        </Datagrid>
      </ReferenceManyField>
    </>
  );
};

const useStyles = makeStyles((theme) => ({
  root: {
    marginBottom: theme.spacing(2),
    '& #section-actions': {
      opacity: 0.5,
      transition: `opacity ${theme.transitions.easing.easeIn}`,
      transitionDuration: `${theme.transitions.duration.shortest}ms`,
    },
    '&:hover': {
      '& #section-actions': {
        opacity: 1,
      },
    },
  },
}));

export default LegalConsentsTab;
